<script setup lang="ts">
import {ref, onMounted, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {GlobeAltIcon} from '@heroicons/vue/24/outline'
import { useRouter, useRoute } from 'vue-router'
import Footer from './components/Footer.vue'

const router = useRouter()
const route = useRoute()
const {t, locale} = useI18n()
const isLanguageMenuOpen = ref(false)
const currentBannerIndex = ref(0)
const isTransitioning = ref(false)
const isMenuOpen = ref(false)

// 监听语言变化，更新页面标题
watch(locale, () => {
  document.title = t('company.name')
})

// 初始化时设置标题
onMounted(() => {
  document.title = t('company.name')
  setInterval(nextSlide, 5000)
})

const bannerImages = [
    '/images/bg1.jpg',
    '/images/bg2.jpg',
    '/images/bg3.jpg',
    '/images/bg4.jpg'
]

const nextSlide = () => {
    if (isTransitioning.value) return
    isTransitioning.value = true
    currentBannerIndex.value = (currentBannerIndex.value + 1) % bannerImages.length
    setTimeout(() => {
        isTransitioning.value = false
    }, 1000)
}

const languages = [
    {code: 'zh', name: '中文'},
    {code: 'en', name: 'English'},
    {code: 'ja', name: '日本語'}
]

const changeLanguage = (lang: string) => {
    locale.value = lang
    isLanguageMenuOpen.value = false
    isMenuOpen.value = false
    localStorage.setItem('locale', lang)
}

const savedLocale = localStorage.getItem('locale')
if (savedLocale) {
    locale.value = savedLocale
}

const handleNavigation = (path: string) => {
    router.push(path)
}
</script>

<template>
    <div class="min-h-screen bg-gray-50">
        <!-- 导航栏 -->
        <nav class="fixed top-0 left-0 right-0 bg-gradient-to-r from-white/90 to-white/70 shadow-lg z-50 backdrop-blur-sm">
            <div class="container">
                <div class="flex justify-between h-20">
                    <div class="flex items-center">
                        <img src="/images/logo.png" alt="Company Logo" class="h-24 w-auto">
                    </div>
                    <!-- 移动端汉堡菜单按钮 -->
                    <button @click="isMenuOpen = !isMenuOpen" class="md:hidden p-2">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-if="!isMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                    <!-- 桌面端导航 -->
                    <div class="hidden md:flex items-center space-x-8">
                        <router-link to="/" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/' }">
                            {{ t('nav.home') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/', 'opacity-0 group-hover:opacity-100': route.path !== '/' }"></span>
                        </router-link>
                        <router-link to="/about" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/about' }">
                            {{ t('nav.about') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/about', 'opacity-0 group-hover:opacity-100': route.path !== '/about' }"></span>
                        </router-link>
                        <a href="#" @click.prevent="handleNavigation('/products')" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/products' }">
                            {{ t('nav.products') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/products', 'opacity-0 group-hover:opacity-100': route.path !== '/products' }"></span>
                        </a>
                        <router-link to="/technology" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/technology' }">
                            {{ t('nav.technology') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/technology', 'opacity-0 group-hover:opacity-100': route.path !== '/technology' }"></span>
                        </router-link>
                        <router-link to="/sustainability" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/sustainability' }">
                            {{ t('nav.sustainability') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/sustainability', 'opacity-0 group-hover:opacity-100': route.path !== '/sustainability' }"></span>
                        </router-link>
                        <router-link to="/contact" class="text-gray-700 hover:text-primary transition-colors duration-300 relative group" :class="{ 'text-primary': route.path === '/contact' }">
                            {{ t('nav.contact') }}
                            <span class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transition-all duration-300" :class="{ 'opacity-100': route.path === '/contact', 'opacity-0 group-hover:opacity-100': route.path !== '/contact' }"></span>
                        </router-link>
                        <div class="relative">
                            <button
                                @click="isLanguageMenuOpen = !isLanguageMenuOpen"
                                class="flex items-center space-x-1 text-gray-700 hover:text-primary focus:outline-none"
                            >
                                <GlobeAltIcon class="h-5 w-5"/>
                                <span>{{ languages.find(lang => lang.code === locale)?.name }}</span>
                            </button>
                            <div
                                v-if="isLanguageMenuOpen"
                                class="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg py-1 z-10"
                            >
                                <button
                                    v-for="lang in languages"
                                    :key="lang.code"
                                    @click="changeLanguage(lang.code)"
                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    {{ lang.name }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- 移动端导航菜单 -->
                <div v-if="isMenuOpen" class="md:hidden bg-white shadow-lg rounded-lg mt-2 p-4">
                    <div class="flex flex-col space-y-4">
                        <router-link to="/" class="text-gray-700 hover:text-primary transition-colors duration-300" @click="isMenuOpen = false">
                            {{ t('nav.home') }}
                        </router-link>
                        <router-link to="/about" class="text-gray-700 hover:text-primary transition-colors duration-300" @click="isMenuOpen = false">
                            {{ t('nav.about') }}
                        </router-link>
                        <a href="#" @click.prevent="handleNavigation('/products'); isMenuOpen = false" class="text-gray-700 hover:text-primary transition-colors duration-300">
                            {{ t('nav.products') }}
                        </a>
                        <router-link to="/technology" class="text-gray-700 hover:text-primary transition-colors duration-300" @click="isMenuOpen = false">
                            {{ t('nav.technology') }}
                        </router-link>
                        <router-link to="/sustainability" class="text-gray-700 hover:text-primary transition-colors duration-300" @click="isMenuOpen = false">
                            {{ t('nav.sustainability') }}
                        </router-link>
                        <router-link to="/contact" class="text-gray-700 hover:text-primary transition-colors duration-300" @click="isMenuOpen = false">
                            {{ t('nav.contact') }}
                        </router-link>
                        <div class="relative">
                            <button
                                @click="isLanguageMenuOpen = !isLanguageMenuOpen"
                                class="flex items-center space-x-1 text-gray-700 hover:text-primary focus:outline-none"
                            >
                                <GlobeAltIcon class="h-5 w-5"/>
                                <span>{{ languages.find(lang => lang.code === locale)?.name }}</span>
                            </button>
                            <div
                                v-if="isLanguageMenuOpen"
                                class="mt-2 w-full bg-white rounded-md shadow-lg py-1"
                            >
                                <button
                                    v-for="lang in languages"
                                    :key="lang.code"
                                    @click="changeLanguage(lang.code)"
                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    {{ lang.name }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
        
        <router-view></router-view>

        <Footer />
    </div>
</template>

<style>
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}


/* 自定义滚动条样式 */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* 平滑滚动 */
html {
    scroll-behavior: smooth;
}

/* 产品卡片悬停效果 */
.group:hover {
    transform: scale(1.1);
}

</style>
